(() => {
  const API_REVISION = '2026-04-15';
  const PRODUCTS = ['NkoroPremium', 'Attack', 'Libi'];
  const RESULT_LABELS = {
    NkoroPremium: 'Nkoro Premium',
    Attack: 'Attack Stack',
    Libi: 'Libi Large'
  };

  function createSubmissionId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }

    return `nkoro-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }

  function validEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  function safeJsonParse(text) {
    try {
      return JSON.parse(text);
    } catch (error) {
      console.error('Nkoro quiz data could not be parsed.', error);
      return null;
    }
  }

  class NkoroQuiz {
    constructor(root) {
      this.root = root;
      this.config = {
        siteId: root.dataset.klaviyoSiteId || '',
        listId: root.dataset.klaviyoListId || '',
        eventName: root.dataset.klaviyoEventName || 'Completed Nkoro Quiz',
        source: root.dataset.klaviyoSource || 'Nkoro website quiz',
        quizVersion: root.dataset.quizVersion || '1.0',
        debug: root.dataset.debug === 'true'
      };

      const dataElement = root.querySelector('[data-nq-questions]');
      const parsed = dataElement ? safeJsonParse(dataElement.textContent) : null;
      this.questions = parsed?.questions || {};
      this.questionKeys = Object.keys(this.questions);

      this.state = {
        currentIndex: 0,
        answers: {},
        scores: {
          NkoroPremium: 0,
          Attack: 0,
          Libi: 0
        },
        result: null,
        submissionId: createSubmissionId(),
        submitting: false
      };

      this.elements = {
        screens: root.querySelectorAll('[data-nq-screen]'),
        intro: root.querySelector('[data-nq-screen="intro"]'),
        questions: root.querySelector('[data-nq-screen="questions"]'),
        capture: root.querySelector('[data-nq-screen="capture"]'),
        result: root.querySelector('[data-nq-screen="result"]'),
        start: root.querySelector('[data-nq-start]'),
        back: root.querySelector('[data-nq-back]'),
        next: root.querySelector('[data-nq-next]'),
        captureBack: root.querySelector('[data-nq-capture-back]'),
        retake: root.querySelector('[data-nq-retake]'),
        questionNumber: root.querySelector('[data-nq-question-number]'),
        question: root.querySelector('[data-nq-question]'),
        answers: root.querySelector('[data-nq-answers]'),
        questionError: root.querySelector('[data-nq-question-error]'),
        progressLabel: root.querySelector('[data-nq-progress-label]'),
        progressPercent: root.querySelector('[data-nq-progress-percent]'),
        progressBar: root.querySelector('[data-nq-progress-bar]'),
        form: root.querySelector('[data-nq-form]'),
        formError: root.querySelector('[data-nq-form-error]'),
        submit: root.querySelector('[data-nq-submit]'),
        submitLabel: root.querySelector('[data-nq-submit-label]'),
        submitLoading: root.querySelector('[data-nq-submit-loading]'),
        resultContent: root.querySelector('[data-nq-result-content]'),
        syncMessage: root.querySelector('[data-nq-sync-message]')
      };

      this.bindEvents();
    }

    bindEvents() {
      this.elements.start?.addEventListener('click', () => {
        this.showScreen('questions');
        this.renderQuestion();
      });

      this.elements.back?.addEventListener('click', () => {
        if (this.state.currentIndex === 0) {
          this.showScreen('intro');
          return;
        }

        this.state.currentIndex -= 1;
        this.renderQuestion();
      });

      this.elements.next?.addEventListener('click', () => {
        const questionKey = this.questionKeys[this.state.currentIndex];
        if (!this.state.answers[questionKey]) {
          this.setQuestionError(true);
          return;
        }

        this.setQuestionError(false);

        if (this.state.currentIndex === this.questionKeys.length - 1) {
          this.showScreen('capture');
          this.elements.form?.querySelector('input[name="first_name"]')?.focus();
          return;
        }

        this.state.currentIndex += 1;
        this.renderQuestion();
      });

      this.elements.captureBack?.addEventListener('click', () => {
        this.state.currentIndex = this.questionKeys.length - 1;
        this.showScreen('questions');
        this.renderQuestion();
      });

      this.elements.form?.addEventListener('submit', (event) => this.handleSubmit(event));
      this.elements.retake?.addEventListener('click', () => this.reset());
    }

    showScreen(name) {
      this.elements.screens.forEach((screen) => {
        screen.hidden = screen.dataset.nqScreen !== name;
      });

      const active = this.root.querySelector(`[data-nq-screen="${name}"]`);
      active?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    renderQuestion() {
      const questionKey = this.questionKeys[this.state.currentIndex];
      const question = this.questions[questionKey];
      const selectedAnswer = this.state.answers[questionKey];
      const questionNumber = this.state.currentIndex + 1;
      const totalQuestions = this.questionKeys.length;
      const percent = Math.round((questionNumber / totalQuestions) * 100);

      this.elements.questionNumber.textContent = `Question ${questionNumber}`;
      this.elements.question.textContent = question.question;
      this.elements.progressLabel.textContent = `Question ${questionNumber} of ${totalQuestions}`;
      this.elements.progressPercent.textContent = `${percent}%`;
      this.elements.progressBar.style.width = `${percent}%`;
      this.elements.answers.innerHTML = '';

      Object.entries(question)
        .filter(([key]) => key !== 'question')
        .forEach(([answerKey, answer]) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'nkoro-quiz__answer';
          button.dataset.answerKey = answerKey;
          button.setAttribute('aria-pressed', String(selectedAnswer === answerKey));
          button.textContent = answer.label;

          button.addEventListener('click', () => {
            this.state.answers[questionKey] = answerKey;
            this.setQuestionError(false);
            this.elements.answers.querySelectorAll('.nkoro-quiz__answer').forEach((answerButton) => {
              answerButton.setAttribute('aria-pressed', String(answerButton === button));
            });
          });

          this.elements.answers.appendChild(button);
        });

      this.elements.back.textContent = this.state.currentIndex === 0
        ? this.elements.back.dataset.labelStart
        : this.elements.back.dataset.labelBack;
      this.elements.next.textContent = this.state.currentIndex === totalQuestions - 1
        ? this.elements.next.dataset.labelContinue
        : this.elements.next.dataset.labelNext;

      window.requestAnimationFrame(() => {
        this.elements.question?.focus?.({ preventScroll: true });
      });
    }

    setQuestionError(show) {
      this.elements.questionError.hidden = !show;
    }

    setFormError(message = '') {
      this.elements.formError.textContent = message;
      this.elements.formError.hidden = !message;
    }

    calculateScores() {
      const scores = {
        NkoroPremium: 0,
        Attack: 0,
        Libi: 0
      };

      this.questionKeys.forEach((questionKey) => {
        const answerKey = this.state.answers[questionKey];
        const answer = this.questions[questionKey]?.[answerKey];
        if (!answer) return;

        PRODUCTS.forEach((product) => {
          scores[product] += Number(answer[product] || 0);
        });
      });

      this.state.scores = scores;
      return scores;
    }

    calculateResult() {
      const scores = this.calculateScores();
      const highestScore = Math.max(...Object.values(scores));
      let tiedProducts = PRODUCTS.filter((product) => scores[product] === highestScore);

      // Ties are resolved using the strongest product score in Q2, then Q8, then Q7.
      // If a tie remains, the fixed order is Nkoro Premium, Attack, then Libi.
      ['Q2', 'Q8', 'Q7'].some((questionKey) => {
        if (tiedProducts.length === 1) return true;

        const answerKey = this.state.answers[questionKey];
        const answer = this.questions[questionKey]?.[answerKey];
        if (!answer) return false;

        const highestTieScore = Math.max(...tiedProducts.map((product) => Number(answer[product] || 0)));
        tiedProducts = tiedProducts.filter((product) => Number(answer[product] || 0) === highestTieScore);
        return tiedProducts.length === 1;
      });

      const result = PRODUCTS.find((product) => tiedProducts.includes(product));
      this.state.result = result;
      return result;
    }

    getAnswerLabel(questionKey) {
      const answerKey = this.state.answers[questionKey];
      return this.questions[questionKey]?.[answerKey]?.label || '';
    }

    getProfileProperties(marketingConsent) {
      return {
        'Nkoro Quiz Completed': true,
        'Nkoro Quiz Result': RESULT_LABELS[this.state.result],
        'Nkoro Quiz Result Code': this.state.result,
        'Nkoro Quiz Version': this.config.quizVersion,
        'Nkoro Quiz Age Group': this.getAnswerLabel('Q1'),
        'Nkoro Quiz Importance': this.getAnswerLabel('Q9'),
        'Nkoro Quiz Marketing Consent': marketingConsent,
        'Nkoro Quiz Source': this.config.source
      };
    }

    getEventProperties(marketingConsent) {
      return {
        result_code: this.state.result,
        result_name: RESULT_LABELS[this.state.result],
        quiz_version: this.config.quizVersion,
        age_group: this.getAnswerLabel('Q1'),
        importance: this.getAnswerLabel('Q9'),
        marketing_consent: marketingConsent,
        source: this.config.source,
        page_url: window.location.href
      };
    }

    async subscribeProfile({ email, firstName, profileProperties }) {
      if (!this.config.listId) {
        throw new Error('The Klaviyo list ID is missing in the Shopify quiz settings.');
      }

      const attributes = {
        email,
        properties: profileProperties,
        subscriptions: {
          email: {
            marketing: {
              consent: 'SUBSCRIBED'
            }
          }
        }
      };

      if (firstName) attributes.first_name = firstName;

      const response = await fetch(
        `https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(this.config.siteId)}`,
        {
          method: 'POST',
          headers: {
            accept: 'application/vnd.api+json',
            'content-type': 'application/vnd.api+json',
            revision: API_REVISION
          },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                custom_source: this.config.source,
                profile: {
                  data: {
                    type: 'profile',
                    attributes
                  }
                }
              },
              relationships: {
                list: {
                  data: {
                    type: 'list',
                    id: this.config.listId
                  }
                }
              }
            }
          })
        }
      );

      if (!response.ok) {
        throw new Error(await this.getApiError(response, 'Klaviyo subscription failed.'));
      }
    }

    async trackQuizEvent({ email, firstName, profileProperties, eventProperties }) {
      const profileAttributes = {
        email,
        properties: profileProperties
      };

      if (firstName) profileAttributes.first_name = firstName;

      const response = await fetch(
        `https://a.klaviyo.com/client/events?company_id=${encodeURIComponent(this.config.siteId)}`,
        {
          method: 'POST',
          headers: {
            accept: 'application/vnd.api+json',
            'content-type': 'application/vnd.api+json',
            revision: API_REVISION
          },
          body: JSON.stringify({
            data: {
              type: 'event',
              attributes: {
                properties: eventProperties,
                unique_id: this.state.submissionId,
                metric: {
                  data: {
                    type: 'metric',
                    attributes: {
                      name: this.config.eventName
                    }
                  }
                },
                profile: {
                  data: {
                    type: 'profile',
                    attributes: profileAttributes
                  }
                }
              }
            }
          })
        }
      );

      if (!response.ok) {
        throw new Error(await this.getApiError(response, 'Klaviyo event tracking failed.'));
      }
    }

    async getApiError(response, fallback) {
      try {
        const payload = await response.json();
        return payload?.errors?.[0]?.detail || payload?.errors?.[0]?.title || fallback;
      } catch (error) {
        return fallback;
      }
    }

    setSubmitting(submitting) {
      this.state.submitting = submitting;
      this.elements.submit.disabled = submitting;
      this.elements.submitLabel.hidden = submitting;
      this.elements.submitLoading.hidden = !submitting;
    }

    showResult() {
      const template = this.root.querySelector(`[data-nq-result-template="${this.state.result}"]`);
      this.elements.resultContent.innerHTML = template ? template.innerHTML : '<p>Your result could not be displayed.</p>';
      this.showScreen('result');
    }

    setSyncMessage(message, type = 'success') {
      this.elements.syncMessage.textContent = message;
      this.elements.syncMessage.className = `nkoro-quiz__message nkoro-quiz__message--${type}`;
      this.elements.syncMessage.hidden = !message;
    }

    async handleSubmit(event) {
      event.preventDefault();
      if (this.state.submitting) return;

      const formData = new FormData(this.elements.form);
      const email = String(formData.get('email') || '').trim().toLowerCase();
      const firstName = String(formData.get('first_name') || '').trim();
      const privacyConsent = formData.get('privacy_consent') === 'on';
      const marketingConsent = formData.get('marketing_consent') === 'on';

      if (!validEmail(email)) {
        this.setFormError('Enter a valid email address.');
        this.elements.form.querySelector('input[name="email"]')?.focus();
        return;
      }

      if (!privacyConsent) {
        this.setFormError('Please confirm that Nkoro may process your quiz answers to provide your result.');
        this.elements.form.querySelector('input[name="privacy_consent"]')?.focus();
        return;
      }

      if (!this.config.siteId) {
        this.setFormError('The Klaviyo Site ID is missing in the Shopify quiz settings.');
        return;
      }

      this.setFormError('');
      this.calculateResult();
      this.showResult();
      this.setSubmitting(true);
      this.setSyncMessage('Saving your result...', 'success');

      const profileProperties = this.getProfileProperties(marketingConsent);
      const eventProperties = this.getEventProperties(marketingConsent);

      if (this.config.debug) {
        console.log('Nkoro quiz result', {
          answers: this.state.answers,
          scores: this.state.scores,
          result: this.state.result,
          profileProperties,
          eventProperties
        });
      }

      try {
        if (marketingConsent) {
          await this.subscribeProfile({ email, firstName, profileProperties });
        }

        await this.trackQuizEvent({ email, firstName, profileProperties, eventProperties });

        this.setSyncMessage(
          marketingConsent
            ? 'Your result has been saved and your email preferences have been recorded.'
            : 'Your result has been saved. You have not been subscribed to marketing emails.',
          'success'
        );
      } catch (error) {
        console.error('Nkoro quiz sync error', error);
        this.setSyncMessage(
          'Your recommendation is shown above, but we could not save it. Please try again later.',
          'error'
        );
      } finally {
        this.setSubmitting(false);
      }
    }

    reset() {
      this.state = {
        currentIndex: 0,
        answers: {},
        scores: {
          NkoroPremium: 0,
          Attack: 0,
          Libi: 0
        },
        result: null,
        submissionId: createSubmissionId(),
        submitting: false
      };

      this.elements.form?.reset();
      this.elements.resultContent.innerHTML = '';
      this.setFormError('');
      this.setQuestionError(false);
      this.setSyncMessage('');
      this.showScreen('intro');
    }
  }

  function init(root) {
    if (!root || root.dataset.nqInitialized === 'true') return;
    root.dataset.nqInitialized = 'true';
    new NkoroQuiz(root);
  }

  function initAll(scope = document) {
    scope.querySelectorAll('[data-nkoro-quiz]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
